import { delay, http, HttpResponse } from "msw";
import { apiPaths } from "../../api-paths.js";
import type {
  Trips_GetTrip_Money,
  Trips_GetTrip_PriceBreakdown,
  Trips_GetTripModifyDateOptions_GetTripModifyDateOptionsQueryResponse_Success,
  Trips_GetTripModifyDateOptions_GetTripModifyDateOptionsQueryResponse_Unprocessable,
  Trips_GetTripModifyDateOptions_HotelDealWithPriceDelta,
} from "../../swagger-types/v1/index.js";

interface GetTripModifyOptionsHandlerFactoryOptions {
  delayMs?: number | "infinite";
}

const waitForFactoryDelay = async ({ delayMs }: GetTripModifyOptionsHandlerFactoryOptions = {}) => {
  if (delayMs === "infinite") {
    await delay("infinite");
    return;
  }

  if (typeof delayMs === "number" && delayMs > 0) {
    await delay(delayMs);
  }
};

const waitUntilRequestAborted = async (request: Request) => {
  await new Promise<void>((resolve) => {
    request.signal.addEventListener("abort", () => resolve(), { once: true });
  });
};

const createMoney = (amount: number, currency = "USD"): Trips_GetTrip_Money => ({
  amount,
  currency,
});

const createPriceBreakdown = (
  total: number,
  currency = "USD",
  breakdown: Trips_GetTrip_PriceBreakdown["breakdown"] = [],
): Trips_GetTrip_PriceBreakdown => ({
  total: createMoney(total, currency),
  currency,
  breakdown,
});

const roundMoney = (amount: number) => Math.round(amount * 100) / 100;

// The deal prices are derived from the default trip in `createTripDetailsResponseMock`
// (rooms 233.26, fees 16.33, taxes 34.41, "Resort fee" 80.32) so that
// room difference + taxes and fees difference always equals the online price difference.
const TRIP_ROOMS_PRICE = 233.26;
const TRIP_FEES_PRICE = 16.33;
const TRIP_TAXES_PRICE = 34.41;
const TAXES_AND_FEES_SHARE_OF_PRICE_DELTA = 0.2;
const RESORT_FEE_PRICE = 81.32;
const CITY_TAX_PRICE = 5;

const createHotelDealWithPriceDelta = (
  index: number,
  totalPriceWithoutHotelFeesPriceDelta: number,
  roomCode = "1",
  roomName = "Deluxe King Room",
): Trips_GetTripModifyDateOptions_HotelDealWithPriceDelta => {
  const checkIn = "2026-12-18";
  const checkOut = "2026-12-21";
  const taxesAndFeesDelta = roundMoney(totalPriceWithoutHotelFeesPriceDelta * TAXES_AND_FEES_SHARE_OF_PRICE_DELTA);
  const roomsPrice = roundMoney(TRIP_ROOMS_PRICE + totalPriceWithoutHotelFeesPriceDelta - taxesAndFeesDelta);
  const feesPrice = roundMoney(TRIP_FEES_PRICE + taxesAndFeesDelta);
  const hotelFeesPrice = createPriceBreakdown(RESORT_FEE_PRICE, "USD", [
    { amount: RESORT_FEE_PRICE, code: "Unknown", description: "Resort fee" },
  ]);
  const hotelFeeTaxesPrice = createPriceBreakdown(CITY_TAX_PRICE, "USD", [
    { amount: CITY_TAX_PRICE, code: "CityTax", description: "City tax" },
  ]);

  return {
    checkoutId: `deal-${index + 1}`,
    roomName,
    roomCode,
    boardCode: "RoomOnly",
    boardDescription: "Room Only",
    rates: [
      {
        roomCode,
        roomName,
        boardCode: "RoomOnly",
        boardDescription: "Room Only",
        checkIn,
        checkOut,
      },
    ],
    cancellationPolicy: {
      type: "Refundable",
      deadline: "2026-12-15T23:59:59Z",
      description: "Free cancellation until deadline",
      penalties: [
        {
          from: "2026-12-16T00:00:00Z",
          to: null,
          amount: createMoney(150),
        },
      ],
    },
    paymentType: "CreditCard",
    dealIndex: index,
    roomIndex: 0,
    roomDealIndex: index,
    price: {
      currency: "USD",
      nightlyPrice: createMoney(roundMoney(roomsPrice / 3)),
      totalRoomPrice: createMoney(roomsPrice),
      totalRoomsPrice: createMoney(roomsPrice),
      totalPriceWithHotelFees: createMoney(
        roundMoney(
          roomsPrice + feesPrice + TRIP_TAXES_PRICE + hotelFeesPrice.total.amount + hotelFeeTaxesPrice.total.amount,
        ),
      ),
      fees: createPriceBreakdown(feesPrice),
      taxes: createPriceBreakdown(TRIP_TAXES_PRICE),
      hotelFees: hotelFeesPrice,
      hotelFeeTaxes: hotelFeeTaxesPrice,
    },
    verificationStatus: "Verified",
    alternativeBedGroups: ["Double", "King"],
    totalPriceWithoutHotelFeesPriceDelta: createMoney(totalPriceWithoutHotelFeesPriceDelta),
    hotelFeesPriceDelta: createMoney(6),
  };
};

const createTripModifyOptionsMock = (
  hotelDeals: Trips_GetTripModifyDateOptions_HotelDealWithPriceDelta[] = [createHotelDealWithPriceDelta(0, 25)],
): Trips_GetTripModifyDateOptions_GetTripModifyDateOptionsQueryResponse_Success => ({
  hotelDeals,
});

const createGetTripModifyOptions200Handler = (
  response: Trips_GetTripModifyDateOptions_GetTripModifyDateOptionsQueryResponse_Success = createTripModifyOptionsMock(),
  options: GetTripModifyOptionsHandlerFactoryOptions = {},
) => [
  http.post(apiPaths.trips.modification.getOptions, async () => {
    await waitForFactoryDelay(options);
    return HttpResponse.json(response, { status: 200 });
  }),
];

const createGetTripModifyOptions422Handler = (
  message = "Trip modifications are not supported",
  options: GetTripModifyOptionsHandlerFactoryOptions = {},
) => [
  http.post(apiPaths.trips.modification.getOptions, async () => {
    await waitForFactoryDelay(options);
    const responseBody: Trips_GetTripModifyDateOptions_GetTripModifyDateOptionsQueryResponse_Unprocessable = {
      message,
    };
    return HttpResponse.json(responseBody, { status: 422 });
  }),
];

const getTripModifyOptions200SingleDealHandler = createGetTripModifyOptions200Handler();
const getTripModifyOptions200MultipleDealsHandler = createGetTripModifyOptions200Handler(
  createTripModifyOptionsMock([createHotelDealWithPriceDelta(0, 25), createHotelDealWithPriceDelta(1, 75)]),
);
const getTripModifyOptions200EmptyHandler = createGetTripModifyOptions200Handler(createTripModifyOptionsMock([]));

const getTripModifyOptions404Handler = [
  http.post(apiPaths.trips.modification.getOptions, async () => {
    return HttpResponse.json({}, { status: 404 });
  }),
];

const getTripModifyOptions422NotSupportedHandler = createGetTripModifyOptions422Handler(
  "Modifications are not supported for this booking",
);

const getTripModifyOptionsLoadingHandler = [
  http.post(apiPaths.trips.modification.getOptions, async ({ request }) => {
    await waitUntilRequestAborted(request);
    return HttpResponse.json(createTripModifyOptionsMock(), { status: 200 });
  }),
];

export const getTripModifyOptionsHandlers = {
  get200SingleDeal: getTripModifyOptions200SingleDealHandler,
  get200MultipleDeals: getTripModifyOptions200MultipleDealsHandler,
  get200Empty: getTripModifyOptions200EmptyHandler,
  get404: getTripModifyOptions404Handler,
  get422NotSupported: getTripModifyOptions422NotSupportedHandler,
  getLoading: getTripModifyOptionsLoadingHandler,
} as const;

export const getTripModifyOptionsHandlersFactory = {
  create200: (
    response?: Trips_GetTripModifyDateOptions_GetTripModifyDateOptionsQueryResponse_Success,
    options?: GetTripModifyOptionsHandlerFactoryOptions,
  ) => {
    return createGetTripModifyOptions200Handler(response, options);
  },
  create422: (message?: string, options?: GetTripModifyOptionsHandlerFactoryOptions) => {
    return createGetTripModifyOptions422Handler(message, options);
  },
  createDeal: createHotelDealWithPriceDelta,
  createMock: createTripModifyOptionsMock,
} as const;
